/*
 * First-touch attribution: where a visitor first came from (UTM tags, ad click ids, the referring
 * site, the landing page). Kept in a `ue_src` cookie on .userevaluation.com for 400 days, so the
 * app's sign-up can store it with the new account. Only the first touch is kept; later visits
 * never overwrite it. No personal data: just where the visit came from.
 */

const KEY = 'ue_src';
const COOKIE_AGE = 400 * 86_400;
const OWN = /(^|\.)userevaluation\.com$/;

export interface Attribution { source: string; medium?: string; campaign?: string; term?: string; content?: string; referrer?: string; landing?: string; at: string }

const cut = (v: string | null | undefined, n = 100) => (v ? v.trim().slice(0, n) : undefined) || undefined;

function readCookie(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)ue_src=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : null;
}

/** Work out the first touch from this page load (null when it's an internal navigation). */
export function touchFrom(search: string, referrer: string, path: string): Attribution | null {
  const q = new URLSearchParams(search);
  let refHost: string | undefined;
  try { refHost = referrer ? new URL(referrer).hostname.replace(/^www\./, '') : undefined; } catch { refHost = undefined; }
  if (refHost && OWN.test(refHost)) refHost = undefined;
  const clickSource = q.get('gclid') ? 'google' : q.get('fbclid') ? 'facebook' : q.get('msclkid') ? 'bing' : q.get('li_fat_id') ? 'linkedin' : undefined;
  const source = cut(q.get('utm_source'), 60) ?? clickSource ?? refHost ?? (q.get('ref') ? 'ambassador' : 'direct');
  const medium = cut(q.get('utm_medium'), 60) ?? (clickSource ? 'cpc' : refHost ? 'referral' : q.get('ref') ? 'ambassador' : undefined);
  return { source, medium, campaign: cut(q.get('utm_campaign')), term: cut(q.get('utm_term')), content: cut(q.get('utm_content')), referrer: refHost, landing: cut(path, 200), at: new Date().toISOString() };
}

/** Keep the first touch this browser saw (a later tagged visit only fills it if nothing was kept). */
export function captureAttribution(search: string, referrer: string, path: string) {
  try {
    if (readCookie() || localStorage.getItem(KEY)) {
      const kept = readCookie() ?? localStorage.getItem(KEY)!;
      writeCookie(kept);
      return;
    }
  } catch { if (readCookie()) return; }
  const touch = touchFrom(search, referrer, path);
  if (!touch) return;
  const value = JSON.stringify(Object.fromEntries(Object.entries(touch).filter(([, v]) => v !== undefined)));
  try { localStorage.setItem(KEY, value); } catch { /* cookie still has it */ }
  writeCookie(value);
}

function writeCookie(value: string) {
  const host = window.location.hostname;
  const domain = host === 'userevaluation.com' || host.endsWith('.userevaluation.com') ? '; domain=.userevaluation.com' : '';
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${KEY}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_AGE}; samesite=lax${domain}${secure}`;
}
